<template>
  <div>
    <div class="small-title title">
      {{ title }}
      <q-tooltip
        >Any {{ title }} in your script will show up as an input field below,<br />
        so you can test with different {{ title }} values.</q-tooltip
      >
    </div>

    <test-input
      v-for="key in keys"
      :key="key.name"
      :key-meta="key"
      :name="key.name"
      class="input-field"
      :value="getTestValue(key)"
      :update-value="value => updateValue({ key: key.name, value })"
      :on-focus="onFieldFocus"
      :on-blur="onFieldBlur"
      :is-selected="selectedFields.includes(key.name)"
      :show-selection-checkbox="showSelectionCheckboxes"
      :on-toggle-selection="onFieldToggle"
      :is-display-only="isDisplayOnly"
    />

    <div v-if="keys.length < 1">
      No used {{ title }}
      <q-tooltip>You do not have any {{ title }} used in your script </q-tooltip>
    </div>
  </div>
</template>
<script lang="ts">
import { Dict, ImportedKeys, KeyMetaWithDefault } from '@/types'
import * as testValueUtils from '@/utils/testValueUtils'
import { defineComponent } from 'vue'

interface Props {
  isDisplayOnly?: boolean
  title: string
  keys: KeyMetaWithDefault[]
  values: Dict
  updateValue: (payload: { key: string; value: string }) => void
  onFieldFocus: (key?: string) => void
  onFieldBlur: (key?: string) => void
  onFieldToggle: (key?: string) => void
  selectedFields: string[]
  showSelectionCheckboxes: boolean
  importedKeys: ImportedKeys
}

export default defineComponent<Props>({
  props: {
    isDisplayOnly: {
      type: Boolean,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    keys: {
      type: Array,
      required: true,
    },
    updateValue: {
      type: Function,
      default: () => undefined,
    },
    values: {
      type: Object,
      required: true,
    },
    onFieldFocus: {
      type: Function,
      default: () => undefined,
    },
    onFieldBlur: {
      type: Function,
      default: () => undefined,
    },
    selectedFields: {
      type: Array,
      default: function () {
        return []
      },
    },
    showSelectionCheckboxes: {
      type: Boolean,
      default: false,
    },
    onFieldToggle: {
      type: Function,
      default: () => undefined,
    },
    importedKeys: {
      type: Object,
      default: () => undefined,
    },
  },

  setup(props: Props) {
    return {
      isAdded(name: string) {
        return props.importedKeys && props.importedKeys.keysToAdd.includes(name)
      },
      isDeleted(name: string) {
        return props.importedKeys && props.importedKeys.keysToDelete.includes(name)
      },
      getTestValue(key: KeyMetaWithDefault) {
        if (!key.initiallyMockable && !(key.name in props.values)) {
          return ''
        }

        return testValueUtils.testValueAsStringOrNull(props.values[key.name], key.type, key.defaultValue)
      },
    }
  },
})
</script>
<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';

.title {
  margin-top: $space-y-sm;
  margin-bottom: $space-y-sm;
}

.input-field {
  margin-bottom: $space-y-sm;
}
</style>
